import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { AuthService, BaseSiteService, ClientTokenService, OccEndpointsService } from '@spartacus/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, publishReplay, refCount, retry, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ComponentService } from '../component.service';
import { ShimanoLanguageService } from '../language/shimano-language.service';

@Injectable({
  providedIn: 'root',
})
export class BaseStoreService {
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  activeLanguage: string;
  activeCurrency: string;
  baseSiteId: string;
  cacheWindowTime = 3600000; // 1 hour
  baseStoreDateFormat$ = new BehaviorSubject<string>(null);

  private cachedBasestoreData$: any = {};

  constructor(
    protected http: HttpClient,
    protected auth: AuthService,
    protected clientToken: ClientTokenService,
    protected baseService: BaseSiteService,
    protected meta: Meta,
    private languageService: ShimanoLanguageService,
    private componentService: ComponentService,
    private occEndpointsService: OccEndpointsService
  ) {
    this.baseService.getActive().subscribe((baseSite) => (this.baseSiteId = baseSite));
    this.languageService.getActive().subscribe((language) => (this.activeLanguage = language));
    this.componentService.activeCurrency$.subscribe((currency) =>
      currency ? (this.activeCurrency = currency) : (this.activeCurrency = 'USD')
    );
  }

  get tokenId(): string {
    let tokenId: string;
    this.clientToken
      .getClientToken()
      .subscribe((tokenIdData) => (tokenId = tokenIdData.access_token))
      .unsubscribe();
    return tokenId;
  }

  getBasestoreData(cache?: boolean, baseStoreUid?: string): Observable<any> {
    const idString = [this.baseSiteId, this.activeLanguage, this.activeCurrency].toString();
    const url = `${this.occUrl.content}${environment.prefix}${this.baseSiteId}`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'FULL');

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    // temp baseStoreUid = baseSiteId
    if (!baseStoreUid) {
      baseStoreUid = this.baseSiteId;
    }

    if (!cache) this.cachedBasestoreData$ = {};

    if (!this.cachedBasestoreData$[idString]) {
      this.cachedBasestoreData$[idString] = this.baseService
        .getActive()
        .pipe(
          switchMap((baseSite) => {
            return (
              baseSite && this.http.get(this.occEndpointsService.buildUrl(`/basestores/${baseSite}?fields=DEFAULT`), {})
            );
          }),
          tap((data: any) => this.baseStoreDateFormat$.next(data.dateFormat))
        )
        .pipe(publishReplay(1, this.cacheWindowTime), refCount(), take(1), catchError(this.errorHandl));
    }

    return this.cachedBasestoreData$[idString];
  }

  getLiveChatData(): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'liveChatEnabled,liveChatId');

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    return this.baseService
      .getActive()
      .pipe(
        switchMap((baseSite) => {
          return baseSite && this.http.get(this.occEndpointsService.buildUrl(`/basestores/${baseSite}`), httpOptions);
        })
      )
      .pipe(retry(1), catchError(this.errorHandl));
  }

  errorHandl(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
